import React from 'react';
import { Link } from 'react-router-dom';
import { Scale, Shield, Users, Heart, Truck, ClipboardCheck } from 'lucide-react';

const PracticeAreasLanding = () => {
  const practiceAreas = [
    {
      title: 'Auto Accidents',
      description: 'Representing victims of car accidents throughout North Carolina and South Carolina. We investigate thoroughly, identify all coverage sources, and fight for maximum compensation.',
      icon: <Scale className="h-12 w-12" />,
      link: '/practice-areas/auto-accidents',
    },
    {
      title: 'Catastrophic Injury Cases',
      description: 'Handling severe injuries including brain trauma, spinal cord damage, and life-altering harm. We provide in-hospital consultations and work to secure ongoing care.',
      icon: <Shield className="h-12 w-12" />,
      link: '/practice-areas/catastrophic-injury',
    },
    {
      title: 'Premises Liability Cases',
      description: 'Holding property owners accountable for unsafe conditions. We understand NC contributory negligence and SC comparative fault rules.',
      icon: <ClipboardCheck className="h-12 w-12" />,
      link: '/practice-areas/premises-liability',
    },
    {
      title: 'Trucking Accidents',
      description: 'Expert handling of complex commercial truck collision cases. We know federal regulations, industry standards, and how to build a winning case.',
      icon: <Truck className="h-12 w-12" />,
      link: '/practice-areas/trucking-accidents',
    },
    {
      title: 'Workers\' Compensation',
      description: 'Protecting the rights of injured workers throughout North Carolina. We guide you through the claims process and fight for the benefits you deserve.',
      icon: <Users className="h-12 w-12" />,
      link: '/practice-areas/workers-compensation',
    },
    {
      title: 'Wrongful Death',
      description: 'Compassionate representation for families who have lost a loved one due to negligence. We handle these sensitive cases with care and thoroughness.',
      icon: <Heart className="h-12 w-12" />,
      link: '/practice-areas/wrongful-death',
    },
  ];

  return (
    <div data-testid="practice-areas-landing-page">
      {/* Hero */}
      <section className="py-10 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-5xl md:text-6xl font-heading font-bold text-slate-900 mb-4 md:mb-6 tracking-tight">
              Practice Areas
            </h1>
            <p className="text-base md:text-xl text-slate-600 leading-relaxed">
              We focus exclusively on personal injury law, representing individuals and families 
              who have been harmed by negligence or wrongful conduct. Our concentrated practice 
              allows us to provide superior knowledge, preparation, and results.
            </p>
          </div>
        </div>
      </section>

      {/* Practice Areas Grid */}
      <section className="py-10 md:py-24 bg-white" data-testid="practice-areas-grid">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
            {practiceAreas.map((area, index) => (
              <Link
                key={index}
                to={area.link}
                className="border border-slate-200 p-6 md:p-8 hover:shadow-lg transition-shadow duration-300 group"
                data-testid={`practice-area-${index}`}
              >
                <div className="text-primary mb-6 group-hover:scale-110 transition-transform duration-300">
                  {area.icon}
                </div>
                <h3 className="text-2xl font-heading font-semibold text-slate-900 mb-4">
                  {area.title}
                </h3>
                <p className="text-slate-600 leading-relaxed mb-4">
                  {area.description}
                </p>
                <span className="text-primary font-semibold hover:underline">
                  Learn More →
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-10 md:py-24 bg-slate-50" data-testid="what-to-expect-section">
        <div className="container mx-auto px-4 md:px-8 max-w-5xl">
          <h2 className="text-2xl md:text-4xl font-heading font-semibold text-slate-900 mb-8 md:mb-12 text-center tracking-tight">
            What to Expect When You Work With Us
          </h2>
          
          <div className="space-y-8">
            <div className="border-l-4 border-primary pl-6">
              <h3 className="text-xl font-heading font-semibold text-slate-900 mb-2">
                Initial Consultation
              </h3>
              <p className="text-slate-600 leading-relaxed">
                We listen carefully to understand what happened and how it has affected your life. 
                We explain your legal options in plain language and answer all your questions. 
                There is no charge for this meeting.
              </p>
            </div>

            <div className="border-l-4 border-primary pl-6">
              <h3 className="text-xl font-heading font-semibold text-slate-900 mb-2">
                Thorough Investigation
              </h3>
              <p className="text-slate-600 leading-relaxed">
                We gather evidence, interview witnesses, consult experts, and build a complete picture 
                of what happened. We identify all potential sources of recovery and prepare every case 
                as if it will go to trial.
              </p>
            </div>

            <div className="border-l-4 border-primary pl-6">
              <h3 className="text-xl font-heading font-semibold text-slate-900 mb-2">
                Clear Communication
              </h3>
              <p className="text-slate-600 leading-relaxed">
                You will always know what is happening with your case. We return calls promptly, 
                provide regular updates, and keep you informed of all developments and decisions.
              </p>
            </div>

            <div className="border-l-4 border-primary pl-6">
              <h3 className="text-xl font-heading font-semibold text-slate-900 mb-2">
                Aggressive Advocacy
              </h3>
              <p className="text-slate-600 leading-relaxed">
                We negotiate firmly with insurance companies and are always prepared to take your case 
                to trial if necessary. Our goal is to secure full and fair compensation for all your losses.
              </p>
            </div>

            <div className="border-l-4 border-primary pl-6">
              <h3 className="text-xl font-heading font-semibold text-slate-900 mb-2">
                Focus on Your Recovery
              </h3>
              <p className="text-slate-600 leading-relaxed">
                While we handle the legal work, you focus on healing. We coordinate with medical providers, 
                handle paperwork, and work to reduce stress during a difficult time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 md:py-24 bg-primary text-white" data-testid="cta-section">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-semibold mb-4 md:mb-6 tracking-tight">
            Ready to Discuss Your Case?
          </h2>
          <p className="text-base md:text-xl text-white/90 mb-6 md:mb-8 leading-relaxed">
            Contact us today for a free, no-obligation consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Link
              to="/contact"
              className="w-full sm:w-auto bg-white text-primary px-6 md:px-8 py-3.5 md:py-4 rounded-sm font-semibold tracking-wider uppercase text-sm shadow-lg hover:bg-slate-50 transition-colors text-center"
              data-testid="cta-consultation"
            >
              Request a Consultation
            </Link>
            <a
              href="tel:+13363541040"
              className="w-full sm:w-auto border-2 border-white text-white px-6 md:px-8 py-3.5 md:py-4 rounded-sm font-semibold tracking-wider uppercase text-sm hover:bg-white/10 transition-colors text-center"
              data-testid="cta-call"
            >
              Call 336-354-1040
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PracticeAreasLanding;
